import { useEffect, useRef } from "react";
import { ModalCloseButton, ModalOverlay, ModalPanel } from "~/components/modal";
import type { NewsDetail } from "@laftel-ip-originals/notion/contracts";
import type { Locale } from "@laftel-ip-originals/notion/locale";

interface NewsModalProps {
  item: NewsDetail;
  locale: Locale;
  loading?: boolean;
  onClose: () => void;
}

export function NewsModal({ item, locale, loading = false, onClose }: NewsModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel className="max-w-[640px]">
        <ModalCloseButton buttonRef={closeRef} />

        {loading ? (
          <>
            <div className="shrink-0 border-b border-[rgba(var(--ip-primary-glow),0.06)] pt-[22px] px-8 pr-16 pb-6 max-md:px-5 max-md:pr-14 max-md:pt-4 max-md:pb-4">
              <div className="skeleton h-[18px] w-[52px] mb-4" />
              <div className="skeleton h-[28px] w-[70%] mb-3" />
              <div className="skeleton h-[14px] w-[90px]" />
            </div>

            <div className="p-8 max-md:p-5 flex flex-col gap-3">
              <div className="skeleton h-[14px] w-full" />
              <div className="skeleton h-[14px] w-[95%]" />
              <div className="skeleton h-[14px] w-[80%]" />
              <div className="skeleton h-[14px] w-full mt-2" />
              <div className="skeleton h-[14px] w-[60%]" />
            </div>
          </>
        ) : (
          <>
            <div className="shrink-0 border-b border-[rgba(var(--ip-primary-glow),0.12)] pt-[22px] px-8 pr-16 pb-6 max-md:px-5 max-md:pr-14 max-md:pt-4 max-md:pb-4">
              <span className="mb-4 inline-block rounded-[2px] bg-[var(--ip-primary)] px-3 py-[3px] text-[10px] font-medium tracking-[0.12em] text-white">
                {item.category[locale]}
              </span>
              <h2 className="mb-2 text-[28px] max-md:text-[22px] font-bold leading-relaxed text-[var(--ip-text)]">
                {item.title[locale] ?? ""}
              </h2>
              <span className="block text-xs tracking-[0.05em] text-[var(--ip-text-muted)]">
                {item.date}
              </span>
            </div>

            <div className="news-modal__body min-h-0 flex-1 overflow-y-auto p-8 max-md:p-5 text-sm max-md:text-[13px] font-light leading-relaxed text-[var(--ip-text-body)]">
              {item.bodyHtml[locale] ? (
                <div
                  className="news-body"
                  dangerouslySetInnerHTML={{ __html: item.bodyHtml[locale] }}
                />
              ) : (
                <p>상세 내용은 준비 중입니다.</p>
              )}
            </div>
          </>
        )}
      </ModalPanel>
    </ModalOverlay>
  );
}
